import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  LayoutGrid,
  Share2,
  FileText,
  Layers,
  Users,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const HERO_IMG =
  "https://images.pexels.com/photos/13722886/pexels-photo-13722886.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = [
  {
    icon: LayoutGrid,
    title: "6-step guided quotes",
    desc: "Customer, rooms & measurements, accessories, tiers and payment plan — built for the showroom floor and on-site.",
  },
  {
    icon: Layers,
    title: "Dynamic tiered pricing",
    desc: "Configure Luxury, Premium and Economy tiers with per-finish rates and a modular multiplier — your prices, your rules.",
  },
  {
    icon: Share2,
    title: "Branded shareable links",
    desc: "Send customers a polished, mobile-first quote page carrying your own logo, colours and payment details.",
  },
  {
    icon: FileText,
    title: "Dual PDF export",
    desc: "One click downloads an exact colour copy and a printer-friendly black & white version.",
  },
  {
    icon: Users,
    title: "Your whole team",
    desc: "Invite teammates with Admin, Manager and Staff roles. Everyone works in one secure workspace.",
  },
  {
    icon: ShieldCheck,
    title: "Price-freeze guarantee",
    desc: "Saved quotations stay locked to the prices of the day — even after you update your master price lists.",
  },
];

export default function Landing() {
  const { user, loading, signIn } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="landing-loader">
        <div className="w-7 h-7 border-2 border-[#C5A880] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen app-bg text-white" data-testid="landing-page">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0A0C10]/70 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg border border-[#C5A880]/50 flex items-center justify-center">
              <span className="font-display font-bold text-[#C5A880]">I</span>
            </div>
            <span className="font-display font-bold tracking-tight">Interiors Quote Maker</span>
          </div>
          <button
            onClick={signIn}
            className="btn-primary !py-2 !px-4 text-sm"
            data-testid="nav-signin-btn"
          >
            Sign in
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center">
        <img
          src={HERO_IMG}
          alt="Modern interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative max-w-6xl mx-auto px-5 w-full pt-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <div className="overline text-[#C5A880] mb-4">For modular interior businesses</div>
            <h1
              className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              style={{ textShadow: "0 2px 24px rgba(0,0,0,0.6)" }}
            >
              Beautiful interior quotations, in minutes.
            </h1>
            <p className="text-white/75 text-base sm:text-lg mt-6 leading-relaxed max-w-xl">
              Build tiered, branded quotations your customers love — with dynamic
              pricing, shareable links and PDF exports. Set up your studio in one click.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-9">
              <button
                onClick={signIn}
                className="btn-primary flex items-center gap-2"
                data-testid="hero-signin-btn"
              >
                Sign in with Google <ArrowRight size={16} />
              </button>
              <span className="text-white/50 text-sm">Free to start · No card required</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 py-24 md:py-32">
        <div className="overline text-[#C5A880] mb-3">Everything you need</div>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight max-w-2xl">
          A quotation tool built around how interior studios actually sell.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="card-luxe"
              data-testid={`feature-card-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-[#C5A880]/10 border border-[#C5A880]/30 flex items-center justify-center mb-4">
                <f.icon size={18} className="text-[#C5A880]" />
              </div>
              <h3 className="font-display font-semibold text-lg mb-2">{f.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="card-luxe mt-16 text-center py-14">
          <h2 className="font-display font-bold text-2xl sm:text-3xl mb-3">
            Ready to make your first quote?
          </h2>
          <p className="text-white/55 mb-7 max-w-md mx-auto">
            Sign in with Google and your workspace is ready instantly — pre-loaded
            with sample finishes, accessories and tiers you can edit.
          </p>
          <button
            onClick={signIn}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="cta-signin-btn"
          >
            Get started — it&apos;s free <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-white/35 text-sm">
        © {new Date().getFullYear()} Interiors Quote Maker
      </footer>
    </div>
  );
}
